import { toast } from "sonner";

// ── Named constants (replacing magic numbers) ──
const HEADER_FONT_SIZE = 14;
const HEADER_ADDR_FONT_SIZE = 8;
const HEADER_SUB_FONT_SIZE = 7;
const TABLE_HEADER_FONT_SIZE = 8;
const TABLE_BODY_FONT_SIZE = 10;
const TABLE_BODY_MIN_FONT = 8;
const PAGE_MARGIN_X = 15;
const PAGE_MARGIN_TOP = 51;
const ROW_PADDING_X = 2;
const ROW_PADDING_Y = 3;
const MIN_ROW_HEIGHT = 7;
const HEADER_COLOR_R = 0;
const HEADER_COLOR_G = 51;
const HEADER_COLOR_B = 153;

const COMPANY = {
  name: "SINGHANIA INTERNATIONAL LIMITED",
  addr: "C-25, Phase-1, Focal Point, Dhandari Kalan, Ludhiana - 141010, Punjab, India",
  email: "Email: info@singhaniainternational.com, anshul@singhaniainternational.com",
  phone: "Phone: +91 161 4611418, +91 9357681418",
  web: "Web: www.sturdfix.com | www.singhaniainternational.com",
  cert: "An ISO 9001, ISO 14001, ISO 45001 and IATF 16949 certified company",
};

// Column width ratios based on content type
const NARROW_KEYS = ["S.No.", "S.N", "S.No", "Date", "Time In", "Time Out", "Status", "Qty", "Shift"];
const MEDIUM_KEYS = ["Ref No.", "Challan / Invoice No.", "Challan No.", "Contact No.", "Vehicle No.", "Key No.", "AWB", "Invoice No.", "Seal No.", "Seal Intact", "Type (In/Out)", "No. of Packets", "Weight", "Department", "Visitor Badge No.", "Gate (Main/Small)"];

function getColumnWidth(label, totalCols, usableW) {
  const isNarrow = NARROW_KEYS.some(k => label.startsWith(k) || label === k);
  const isMedium = MEDIUM_KEYS.some(k => label.includes(k) || label === k);

  // Assign relative weights
  let weight;
  if (isNarrow) weight = 1;
  else if (isMedium) weight = 1.5;
  else weight = 2.2; // Wide columns for names, descriptions, purposes

  return weight;
}

function calculateColumnWidths(columns, usableW) {
  const weights = columns.map(col => getColumnWidth(col, columns.length, usableW));
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  return weights.map(w => (w / totalWeight) * usableW);
}

function wrapText(doc, text, maxWidth) {
  if (!text) return [""];
  return doc.splitTextToSize(String(text), maxWidth - 2);
}

function drawCompanyHeader(doc, pageW) {
  const cx = pageW / 2;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(HEADER_FONT_SIZE);
  doc.setTextColor(HEADER_COLOR_R, HEADER_COLOR_G, HEADER_COLOR_B);
  doc.text(COMPANY.name, cx, 12, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(HEADER_ADDR_FONT_SIZE);
  doc.setTextColor(60, 60, 60);
  doc.text(COMPANY.addr, cx, 17, { align: "center" });
  doc.setFontSize(HEADER_SUB_FONT_SIZE);
  doc.text(`${COMPANY.email}   ${COMPANY.phone}`, cx, 21, { align: "center" });
  doc.text(COMPANY.web, cx, 25, { align: "center" });
  doc.setDrawColor(HEADER_COLOR_R, HEADER_COLOR_G, HEADER_COLOR_B);
  doc.setLineWidth(0.4);
  doc.line(10, 27, pageW - 10, 27);
  doc.setFontSize(HEADER_SUB_FONT_SIZE);
  doc.setTextColor(0, 128, 64);
  doc.text(COMPANY.cert, cx, 31, { align: "center" });
  return 33;
}

export async function generateReportPDF({
  fgatNo = "",
  title = "",
  columns = [],
  entries = [],
  location = "",
  dateFrom = "",
  dateTo = "",
  filename = "report",
}) {
  if (!entries.length) { toast.error("No data to export"); return; }

  // Lazy load jsPDF to avoid mobile memory issues
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.width;  // ~297mm
  const pageH = doc.internal.pageSize.height; // ~210mm
  const margin = 10;
  const usableW = pageW - margin * 2; // ~277mm
  const colWidths = calculateColumnWidths(columns, usableW);

  const HEADER_FONT = 10;
  const DATA_FONT = 9;
  const CELL_PAD = 1.5;
  const LINE_HEIGHT = 4;

  let pageNum = 0;

  function startNewPage() {
    if (pageNum > 0) doc.addPage();
    pageNum++;

    let y = drawCompanyHeader(doc, pageW);

    // F-GAT No + Title
    doc.setTextColor(200, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    if (fgatNo) doc.text(fgatNo, margin, y + 4);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(title, pageW / 2, y + 4, { align: "center" });

    // Filter line
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    const parts = [`Location: ${location || "All"}`];
    if (dateFrom) parts.push(`From: ${dateFrom}`);
    if (dateTo) parts.push(`To: ${dateTo}`);
    const now = new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: true });
    parts.push(`Generated: ${now}`);
    doc.text(parts.join("  |  "), pageW / 2, y + 9, { align: "center" });
    doc.text(`Page ${pageNum}`, pageW - margin, y + 9, { align: "right" });

    y += 13;
    return y;
  }

  function drawTableHeader(y) {
    // Calculate wrapped header heights
    doc.setFont("helvetica", "bold");
    doc.setFontSize(HEADER_FONT);
    let maxLines = 1;
    const wrappedHeaders = columns.map((col, i) => {
      const lines = wrapText(doc, col, colWidths[i]);
      if (lines.length > maxLines) maxLines = lines.length;
      return lines;
    });
    const rowH = maxLines * (LINE_HEIGHT + 0.5) + CELL_PAD * 2;

    // Draw header background
    doc.setFillColor(27, 60, 135);
    doc.rect(margin, y, usableW, rowH, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(HEADER_FONT);

    let x = margin;
    wrappedHeaders.forEach((lines, i) => {
      lines.forEach((line, li) => {
        doc.text(line, x + CELL_PAD, y + CELL_PAD + 3 + li * (LINE_HEIGHT + 0.5));
      });
      x += colWidths[i];
    });

    // Draw column separators
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.2);
    x = margin;
    for (let i = 0; i < columns.length - 1; i++) {
      x += colWidths[i];
      doc.line(x, y, x, y + rowH);
    }

    doc.setTextColor(0, 0, 0);
    return y + rowH;
  }

  function drawDataRow(y, entry, idx) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(DATA_FONT);

    // Calculate row height from wrapped text
    let maxLines = 1;
    const wrappedCells = columns.map((col, i) => {
      const val = String(entry[col] ?? "");
      const lines = wrapText(doc, val || "\u2014", colWidths[i]);
      if (lines.length > maxLines) maxLines = lines.length;
      return lines;
    });
    const rowH = maxLines * LINE_HEIGHT + CELL_PAD * 2;

    // Alternating row background
    if (idx % 2 === 0) {
      doc.setFillColor(245, 246, 250);
      doc.rect(margin, y, usableW, rowH, "F");
    }

    // Draw row border
    doc.setDrawColor(220, 220, 230);
    doc.setLineWidth(0.1);
    doc.line(margin, y + rowH, margin + usableW, y + rowH);

    // Draw cell text
    doc.setTextColor(30, 30, 30);
    let x = margin;
    wrappedCells.forEach((lines, i) => {
      lines.forEach((line, li) => {
        doc.text(line, x + CELL_PAD, y + CELL_PAD + 3 + li * LINE_HEIGHT);
      });
      x += colWidths[i];
    });

    return y + rowH;
  }

  // Start generating
  let y = startNewPage();
  y = drawTableHeader(y);

  entries.forEach((entry, idx) => {
    // Pre-calculate row height to check page break
    doc.setFont("helvetica", "normal");
    doc.setFontSize(DATA_FONT);
    let maxLines = 1;
    columns.forEach((col, i) => {
      const lines = wrapText(doc, String(entry[col] ?? "") || "\u2014", colWidths[i]);
      if (lines.length > maxLines) maxLines = lines.length;
    });
    const rowH = maxLines * LINE_HEIGHT + CELL_PAD * 2;

    if (y + rowH > pageH - 12) {
      y = startNewPage();
      y = drawTableHeader(y);
    }

    y = drawDataRow(y, entry, idx);
  });

  doc.save(`${filename}.pdf`);
  toast.success("PDF exported");
}

export async function generateAnalyticsPDF({ analytics, location, dateFrom, dateTo }) {
  if (!analytics?.length) { toast.error("No analytics data"); return; }
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.width;
  const margin = 10;

  let y = drawCompanyHeader(doc, pageW);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Visitor Analytics \u2014 Company-wise Analysis", pageW / 2, y + 5, { align: "center" });
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  const now = new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  doc.text(`Location: ${location || "All"} | Generated: ${now}`, pageW / 2, y + 10, { align: "center" });
  y += 16;

  analytics.forEach((comp) => {
    if (y > 185) { doc.addPage(); y = 15; }
    doc.setFillColor(27, 60, 135);
    doc.rect(margin, y, pageW - margin * 2, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(`${comp.company} \u2014 ${comp.total_visits} visits, ${comp.total_hours} hrs`, margin + 2, y + 5);
    doc.setTextColor(0, 0, 0);
    y += 9;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    comp.visitors.forEach((v) => {
      if (y > 190) { doc.addPage(); y = 15; }
      doc.text(`    ${v.name} (${v.mobile || "\u2014"}) \u2014 ${v.visits} visits, ${v.hours} hrs`, margin + 4, y + 3);
      y += 5.5;
    });
    y += 4;
  });

  doc.save(`Visitor_Analytics_${new Date().toISOString().slice(0, 10)}.pdf`);
  toast.success("Analytics PDF exported");
}

export async function generateShiftSummaryPDF({ summary }) {
  if (!summary) { toast.error("No summary to export"); return; }
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.width;
  const pageH = doc.internal.pageSize.height;
  const margin = 12;
  const rupee = (n) => `Rs. ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  let y = drawCompanyHeader(doc, pageW);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`Shift Summary Report \u2014 Shift ${summary.shift_label}`, pageW / 2, y + 5, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(
    `${summary.location} | Date: ${summary.date} | Generated: ${new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}`,
    pageW / 2, y + 10, { align: "center" },
  );
  y += 17;
  doc.setTextColor(0, 0, 0);

  function sectionTitle(text) {
    if (y > pageH - 30) { doc.addPage(); y = 15; }
    doc.setFillColor(27, 60, 135);
    doc.rect(margin, y, pageW - margin * 2, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(text, margin + 2, y + 5);
    doc.setTextColor(0, 0, 0);
    y += 10;
  }

  function line(text, indent = 2, bold = false) {
    if (y > pageH - 15) { doc.addPage(); y = 15; }
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(9);
    doc.text(text, margin + indent, y);
    y += 5;
  }

  sectionTitle(`Entries Logged: ${summary.total_entries}  |  Purchase Value: ${rupee(summary.purchase.total_with_gst)} (with GST)`);
  summary.modules.filter(m => m.count > 0).forEach(m => line(`${m.label}: ${m.count}`));
  if (!summary.modules.some(m => m.count > 0)) line("No entries logged in this shift.");
  y += 3;

  sectionTitle("Purchase Value By Module");
  summary.purchase.modules.forEach(m => line(`${m.label}: ${rupee(m.with_gst)} with GST | ${rupee(m.without_gst)} without GST | ${m.entries} entries`));
  line(`TOTAL: ${rupee(summary.purchase.total_with_gst)} with GST | ${rupee(summary.purchase.total_without_gst)} without GST`, 2, true);
  y += 3;

  summary.pending.forEach(block => {
    sectionTitle(`${block.label} (${block.count})`);
    if (block.items.length === 0) line("Nothing pending.");
    block.items.forEach(item => line(`${item.ref_no || "\u2014"}  ${item.primary || ""}  ${item.secondary ? `\u2014 ${item.secondary}` : ""}`));
    y += 3;
  });

  sectionTitle("Guard Sign-Off");
  if (summary.handover) {
    line(`Outgoing: ${summary.handover.outgoing_name || summary.handover.outgoing_guard || "\u2014"}`);
    line(`Incoming: ${summary.handover.incoming_name || "\u2014"} ${summary.handover.incoming_acknowledged ? "(acknowledged)" : "(pending acknowledgement)"}`);
    if (summary.handover.outgoing_remarks) line(`Remarks: ${summary.handover.outgoing_remarks}`);
  } else {
    line("No handover signed off for this shift yet.");
  }

  doc.save(`Shift_Summary_${summary.date}_${summary.shift}.pdf`);
  toast.success("Shift summary PDF exported");
}


export { COMPANY };
